import type { RequestFn } from '../http';

export type CallbackStatus = 'OPEN' | 'IN_PROGRESS' | 'DONE' | 'NO_CALLBACK';

export interface CallbackUrgency {
  level: 'HOCH' | 'MITTEL' | 'NORMAL';
  /** Klartext-Begründungen — ein Ampelwort allein wird nach kurzer Zeit übersehen. */
  reasons: string[];
}

export interface CallbackUserRef {
  id: string;
  displayName: string;
}

export interface CallbackTask {
  id: string;
  phoneE164: string;
  displayName: string | null;
  customerId: string | null;
  customer: {
    id: string;
    name: string;
    isVip: boolean;
    customerType: string;
    phone?: string | null;
    branch?: string | null;
  } | null;
  contactId: string | null;
  unmatched: boolean;
  firstCallAt: string;
  lastCallAt: string;
  callCount: number;
  callsSinceHandled: number;
  lastSummary: string | null;
  lastConversationId: string | null;
  status: CallbackStatus;
  assignedToId: string | null;
  assignedTo: CallbackUserRef | null;
  dueAt: string | null;
  handledById: string | null;
  handledBy: CallbackUserRef | null;
  handledAt: string | null;
  resolutionNote: string | null;
  /** Aus dem Telefonanlagen-Abgleich: letzter Rückrufversuch zu dieser Nummer. */
  lastOutboundAt: string | null;
  lastOutboundById: string | null;
  lastOutboundBy: CallbackUserRef | null;
  lastOutboundAnswered: boolean;
  outboundAttempts: number;
  /** Letzter eingehender Anruf, den jemand angenommen hat. */
  lastInboundAnsweredAt: string | null;
  createdAt: string;
  updatedAt: string;
  openTicketCount: number;
  urgency: CallbackUrgency;
}

/** Ein Gespräch aus dem Anrufverlauf der Telefonanlage. */
export interface WebexCallEntry {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND' | string;
  startedAt: string;
  answered: boolean;
  durationSec: number | null;
  outcome: string | null;
  webexUser: string | null;
  user: CallbackUserRef | null;
}

export interface CallbackEvent {
  id: string;
  taskId: string;
  kind: string;
  message: string;
  userId: string | null;
  user: CallbackUserRef | null;
  createdAt: string;
}

export interface CallbackCallLogEntry {
  id: string;
  requestedAt: string;
  responseMatch: boolean;
  responseStatus: number;
}

/** Nachricht des KI-Telefonassistenten inkl. Transkript und ggf. erzeugtem Ticket. */
export interface CallbackKianMessage {
  id: string;
  createdAt: string;
  source: string | null;
  status: string | null;
  ticketId: string | null;
  ticketProvider: string | null;
  customerMatched: boolean;
  subject: string | null;
  summary: string | null;
  transcript: string | null;
  durationSeconds: number | null;
}

export interface CallbackOpenTicket {
  id: string;
  ticketNumber: string | null;
  subject: string | null;
  status: string | null;
  priority: string | null;
  responsibleUserName: string | null;
  createdDate: string | null;
}

export interface CallbackDetail {
  task: CallbackTask & { events: CallbackEvent[] };
  contact: {
    id: string;
    name: string;
    firstName: string | null;
    lastName: string | null;
    jobRole: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  calls: CallbackCallLogEntry[];
  openTickets: CallbackOpenTicket[];
  webexCalls: WebexCallEntry[];
  kianMessages: CallbackKianMessage[];
}

// ── Webex-Einrichtung (Admin) ─────────────────────────────────────────────────

export interface WebexStatus {
  configured: boolean;
  enabled: boolean;
  mode: 'service-app' | 'static-token' | 'none';
  hasRefreshToken: boolean;
  hasAccessToken: boolean;
  accessTokenExpiresAt: string | null;
  encryptionAvailable: boolean;
  cdrUrl: string;
  locationIds: string[];
  /** Welche Angaben zur Service-App noch fehlen (leer, wenn vollständig). */
  missing: string[];
  /** true = es liegt genug für einen Verbindungstest vor (auch unvollständig). */
  testable: boolean;
}

/** Ein Gesprächs-Auszug aus dem Verbindungstest. */
export interface WebexTestSample {
  startedAt: string;
  direction: 'INBOUND' | 'OUTBOUND';
  otherPartyE164: string;
  webexUser: string | null;
  answered: boolean;
  durationSec: number | null;
}

export interface WebexAdminState {
  status: WebexStatus;
  /** Bis wohin der Anrufverlauf abgeglichen ist. */
  cursor: string | null;
  callCount: number;
  lastCallAt: string | null;
}

export interface SaveWebexPayload {
  clientId?: string;
  clientSecret?: string;
  refreshToken?: string;
  accessToken?: string;
  locationIds?: string[];
  cdrUrl?: string;
  enabled?: boolean;
}

export interface WebexSyncSummary {
  ran: boolean;
  reason?: string;
  windows: number;
  fetched: number;
  stored: number;
  matchedTasks: number;
  closed: number;
  attempts: number;
  inboundAnswered: number;
  skipped: number;
  cursor: string | null;
}

export interface CallbacksListParams {
  status?: CallbackStatus[];
  assignee?: 'me' | 'unassigned' | string;
  due?: 'overdue' | 'today';
  q?: string;
  sort?: 'urgency' | 'recent';
  cursor?: string;
  limit?: number;
}

export interface CallbacksListResponse {
  items: CallbackTask[];
  nextCursor: string | null;
  total: number;
}

export interface CallbacksStats {
  open: number;
  inProgress: number;
  mine: number;
  overdue: number;
  /** Offene Aufgaben, bei denen seit der letzten Erledigung mehrfach angerufen wurde. */
  repeated: number;
}

export interface UpdateCallbackPayload {
  status?: CallbackStatus;
  assignedToId?: string | null;
  dueAt?: string | null;
  resolutionNote?: string | null;
  displayName?: string | null;
}

export function createCallbacksApi(request: RequestFn) {
  return {
    async listCallbacks(params: CallbacksListParams = {}) {
      const qs = new URLSearchParams();
      if (params.status?.length) qs.set('status', params.status.join(','));
      if (params.assignee) qs.set('assignee', params.assignee);
      if (params.due) qs.set('due', params.due);
      if (params.q) qs.set('q', params.q);
      if (params.sort) qs.set('sort', params.sort);
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.limit) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return request<CallbacksListResponse>(`/callbacks${query ? `?${query}` : ''}`);
    },

    async getCallbacksStats() {
      return request<CallbacksStats>('/callbacks/stats');
    },

    async getCallback(id: string) {
      return request<CallbackDetail>(`/callbacks/${id}`);
    },

    async updateCallback(id: string, payload: UpdateCallbackPayload) {
      return request<{ task: CallbackTask }>(`/callbacks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
    },

    async getWebexAdminState() {
      return request<WebexAdminState>('/admin/webex');
    },

    async saveWebexConfig(payload: SaveWebexPayload) {
      return request<{ status: WebexStatus }>('/admin/webex', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    },

    async deleteWebexConfig() {
      return request<{ status: WebexStatus }>('/admin/webex', { method: 'DELETE' });
    },

    async testWebexConnection() {
      return request<{
        ok: boolean;
        message: string;
        sampleCount?: number;
        samples?: WebexTestSample[];
        windowDays?: number;
      }>('/admin/webex/test', { method: 'POST' });
    },

    async runWebexSync() {
      return request<WebexSyncSummary>('/admin/webex/sync', { method: 'POST' });
    },

    async addCallbackNote(id: string, message: string) {
      return request<{ event: CallbackEvent }>(`/callbacks/${id}/notes`, {
        method: 'POST',
        body: JSON.stringify({ message }),
      });
    },
  };
}
